// =================================================================================
// File: src/components/Billing/PromoTokenInput.jsx
// Description: Promo token input and status display for subscriptions
// =================================================================================

import React, { useState } from "react";
import { Ticket, CheckCircle, XCircle } from "lucide-react";
import { supabase } from "../../supabaseClient";
import { useEntitlements } from "../../contexts/EntitlementsContext";
import { useAccess } from "../../contexts/AccessContext";
import { invalidateEntitlements } from "../../lib/entitlementsCache";

/**
 * Map error codes from redeem_promo RPC to friendly user messages
 */
function mapErrorMessage(errorCode, fallback) {
  const errorMap = {
    INVALID_CODE: "This promo code is not valid or has expired.",
    ALREADY_REDEEMED: "You have already used this promo code on this account.",
    NOT_AUTHENTICATED: "Please log in to apply a promo code.",
    CODE_NOT_FOUND: "This promo code does not exist.",
    MAX_REDEMPTIONS_EXCEEDED: "This promo code has reached its maximum redemptions.",
    USER_LIMIT_EXCEEDED: "You have reached the limit for this promo code.",
  };
  return errorMap[errorCode] || fallback || "Unable to apply promo code.";
}

/**
 * PromoTokenInput - Input field for promo codes with validation and status display
 *
 * @param {Object} props
 * @param {string} props.value - Current promo code value
 * @param {Function} props.onChange - Callback when promo code changes
 * @param {boolean} [props.showInlineValidation=true] - Whether to render the inline validation card
 * @param {Function} props.onApplied - Callback when promo is successfully applied
 */
export default function PromoTokenInput({
  value,
  onChange,
  showInlineValidation = true,
  onApplied,
}) {
  const [promoStatus, setPromoStatus] = useState("idle"); // 'idle' | 'loading' | 'success' | 'error'
  const [promoMessage, setPromoMessage] = useState(""); // User-facing message
  const [promoDetail, setPromoDetail] = useState(""); // Additional detail (days remaining, etc.)
  const entitlements = useEntitlements() || {};
  const { refreshAccess } = useAccess() || {};

  const handleChange = (newValue) => {
    onChange?.(newValue);
    // Clear message when user edits
    if (promoStatus !== "idle") {
      setPromoStatus("idle");
      setPromoMessage("");
      setPromoDetail("");
    }
  };

  const handleApply = async () => {
    const normalized = String(value || "").trim().toUpperCase();

    if (!normalized) {
      setPromoStatus("error");
      setPromoMessage("Please enter a promo code.");
      setPromoDetail("");
      return;
    }

    setPromoStatus("loading");
    setPromoMessage("");
    setPromoDetail("");

    try {
      const { data, error } = await supabase.rpc("redeem_promo", {
        code_input: normalized,
      });

      if (error) {
        console.error("[PromoTokenInput] redeem_promo RPC error:", error);
        setPromoStatus("error");
        setPromoMessage(
          error.message || "Unable to apply promo code. Please try again."
        );
        setPromoDetail("");
        return;
      }

      const payload = data || {};

      // RPC returned error flag
      if (!payload.ok) {
        const friendlyMsg = mapErrorMessage(
          payload.error_code,
          payload.error || "Unable to apply promo code."
        );
        console.warn(
          "[PromoTokenInput] redeem_promo returned ok=false:",
          payload.error_code,
          payload.error
        );
        setPromoStatus("error");
        setPromoMessage(friendlyMsg);
        setPromoDetail("");
        return;
      }

      // Success
      const successMsg = payload.message || `${normalized} applied successfully`;
      const detail =
        payload.days_remaining !== undefined && payload.days_remaining !== null
          ? `Unlocked: ${payload.days_remaining} day${payload.days_remaining === 1 ? "" : "s"} remaining`
          : payload.access_until
          ? `Access until ${new Date(payload.access_until).toLocaleDateString()}`
          : "";

      setPromoStatus("success");
      setPromoMessage(successMsg);
      setPromoDetail(detail);

      // Invalidate entitlements cache to force fresh fetch on next access
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user?.id) {
          invalidateEntitlements(user.id);
        }
      } catch (err) {
        console.error("[PromoTokenInput] failed to invalidate cache:", err);
      }

      // Refresh entitlements to update TopBar immediately
      try {
        await entitlements.refresh?.();
      } catch (refreshError) {
        console.error("[PromoTokenInput] entitlements refresh failed:", refreshError);
      }

      // Refresh access state (triggers all gates, TopBar, etc.)
      try {
        await refreshAccess?.();
      } catch (accessRefreshError) {
        console.error("[PromoTokenInput] access refresh failed:", accessRefreshError);
      }

      // Broadcast refresh event for other listeners
      try {
        window.dispatchEvent?.(new Event("mudo:entitlements-refresh"));
      } catch {}

      // Notify parent
      onApplied?.({ ...payload, message: successMsg, detail });
    } catch (error) {
      console.error("[PromoTokenInput] redeem_promo exception:", error);
      setPromoStatus("error");
      setPromoMessage(error.message || "Validation failed. Please try again.");
      setPromoDetail("");
    }
  };


  const isLoading = promoStatus === "loading";
  const isSuccess = promoStatus === "success";
  const isError = promoStatus === "error";

  return (
    <div className="space-y-4">
      {/* Promo Code Input */}
      <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <Ticket className="w-5 h-5 text-orange-400" />
          <h4 className="font-semibold text-white">Have a promo code?</h4>
        </div>

        <div className="flex gap-2">
          <input
            type="text"
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            placeholder="Enter code"
            className="flex-1 px-4 py-2 bg-black/30 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-orange-400/50 focus:ring-2 focus:ring-orange-400/20 transition-all uppercase"
            disabled={isLoading}
            onKeyPress={(e) => e.key === "Enter" && handleApply()}
          />
          <button
            onClick={handleApply}
            disabled={isLoading || !value?.trim()}
            className="px-6 py-2 bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-500 hover:to-red-500 disabled:from-gray-600 disabled:to-gray-700 disabled:cursor-not-allowed rounded-lg font-semibold transition-all"
          >
            {isLoading ? "Applying..." : "Apply"}
          </button>
        </div>

        {/* Status Message */}
        {showInlineValidation && promoMessage && (
          <div className="mt-3 flex items-start gap-2">
            {isSuccess && (
              <>
                <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-green-300 font-medium text-sm">{promoMessage}</p>
                  {promoDetail && (
                    <p className="text-green-200 text-xs mt-1">{promoDetail}</p>
                  )}
                </div>
              </>
            )}
            {isError && (
              <>
                <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-red-300 text-sm">{promoMessage}</p>
                  {promoDetail && (
                    <p className="text-red-200 text-xs mt-1">{promoDetail}</p>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
